import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ReactElement, useEffect, useState } from 'react';
import { ROUTES, URN_QDOS_S3 } from '../../helpers/constans';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { thingQdosActions } from '../../redux/slices/thing-qdos';
import { styles } from './things-qdos-media.styles';
import { shareActions } from '../../redux/slices/share.slice';
import AddChipButton from '../../components/add-chip-button/add-chip-button';

const ThingsQdosMedia = (): ReactElement => {
  const { posts } = useSelector((state: RootState) => state.thingsQdos);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: itemId } = useParams();

  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [portraitMap, setPortraitMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!itemId) return;
    dispatch(thingQdosActions.getAllPostsByItemId(itemId));
  }, [dispatch, itemId]);

  const isCurrentThing = true;
  const qdosEventId = posts?.[0]?.post?.eventId;

  const handleBack = (): void => {
    navigate(ROUTES.things);
  };

  const getMediaUrl = (value?: string): string => {
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${URN_QDOS_S3}${value}`;
  };

  const getImageKey = (entry: any): string => {
    return entry?._id || entry?.post?._id || entry?.post?.imageId || 'unknown-image';
  };

  const handleImageLoad =
    (key: string) =>
    (event: React.SyntheticEvent<HTMLImageElement>): void => {
      const img = event.currentTarget;
      const isPortrait = img.naturalHeight > img.naturalWidth;

      setPortraitMap((prev) => {
        if (prev[key] === isPortrait) return prev;
        return { ...prev, [key]: isPortrait };
      });
    };

  const handleAddJoy = (entry: any): void => {
    const post = entry?.post || {};

    const qdosItemId = post?.itemIds?.[0];
    const eventId = post?.eventId;
    const qdosPostId = post?._id;

    if (!qdosItemId || !eventId || !qdosPostId) {
      console.log('JOY CLICK MISSING DATA', {
        qdosItemId,
        eventId,
        qdosPostId,
        entry,
        post,
      });
      return;
    }

    dispatch(
      shareActions.addMemorableMoment({
        qdosItemId,
        qdosEventId: eventId,
        qdosPostId,
        alternativeId: post?.alternativeId,
        mediaType: post?.mediaType,
        imageId: post?.imageId,
        videoUrl: post?.videoUrl,
        description: post?.description,
        eventTitle: post?.eventTitle,
      }),
    );
  };

  const handleAddToShare = (
    qdosItemId?: string,
    eventId?: string,
    shareType?: 'team' | 'fan',
  ): void => {
    if (!qdosItemId || !eventId || !shareType) return;

    dispatch(
      shareActions.addToShare({
        qdosItemId,
        qdosEventId: eventId,
        shareType,
      }),
    );
  };

  const handleViewTeamShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.teamShare, { eventId: qdosEventId }));
  };

  const handleViewFanShare = (): void => {
    if (!qdosEventId) return;
    navigate(generatePath(ROUTES.fanShare, { eventId: qdosEventId }));
  };

  const renderMedia = (entry: any, enlarged = false): ReactElement => {
    const post = entry?.post || {};
    const imageUrl = getMediaUrl(post?.imageId);
    const videoUrl = getMediaUrl(post?.videoUrl);
    const imageKey = getImageKey(entry);
    const isPortrait = portraitMap[imageKey];

    if (videoUrl) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
          }}
        >
          <video
            controls
            style={{
              display: 'block',
              width: '100%',
              maxHeight: enlarged ? '75vh' : '260px',
              objectFit: 'contain',
              backgroundColor: '#000',
            }}
          >
            <source src={videoUrl} />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    }

    if (imageUrl) {
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={post?.eventTitle || 'QDOS media'}
          onLoad={handleImageLoad(imageKey)}
          sx={{
            width: '100%',
            height: enlarged ? 'auto' : '220px',
            maxHeight: enlarged ? '75vh' : '220px',
            objectFit: enlarged ? 'contain' : isPortrait ? 'contain' : 'cover',
            borderRadius: '8px',
            display: 'block',
            backgroundColor: '#f5f5f5',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '100%',
          minHeight: enlarged ? '260px' : '220px',
          borderRadius: '8px',
          border: '1px dashed #bbb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '1rem',
          color: '#777',
          textAlign: 'center',
          backgroundColor: '#fafafa',
        }}
      >
        <Typography>{post?.alternativeId || 'No media preview available'}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={styles.container}>
      {!isCurrentThing ? (
        <Box sx={{ mt: '2rem' }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <Box sx={styles.mainBox}>
          <Box sx={styles.mainTitleBox}>
            <Box>
              <Typography sx={styles.mainTitle}>QDOS Media</Typography>
              <Typography sx={styles.mainSubTitle}>Posts from QDOS</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button sx={styles.backButton} onClick={handleBack}>
                back
              </Button>
              <Button sx={styles.backButton} onClick={handleViewTeamShare}>
                View Team Share
              </Button>
              <Button sx={styles.backButton} onClick={handleViewFanShare}>
                View Fan Share
              </Button>
            </Box>
          </Box>

          <Box sx={styles.mainContentBox}>
            {[...(posts || [])]
              .slice()
              .reverse()
              .map((el: any) => {
                const post = el?.post || {};
                const qdosItemId = post?.itemIds?.[0];
                const eventId = post?.eventId;

                return (
                  <Box
                    key={el._id}
                    onClick={() => setSelectedItem(el)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      p: '1rem',
                      cursor: 'pointer',
                      backgroundColor: '#fff',
                    }}
                  >
                    {renderMedia(el)}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {post?.eventTitle || 'Untitled event'}
                      </Typography>

                      {post?.description && (
                        <Typography variant="body2" sx={{ color: '#555' }}>
                          {post.description}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <AddChipButton
                        label="Joy"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddJoy(el);
                        }}
                      />

                      <AddChipButton
                        label="Team Share"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(qdosItemId, eventId, 'team');
                        }}
                      />

                      <AddChipButton
                        label="Fan Share"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAddToShare(qdosItemId, eventId, 'fan');
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogContent sx={{ position: 'relative', p: '1rem' }}>
          <IconButton
            onClick={() => setSelectedItem(null)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>

          {selectedItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', pt: '2rem' }}>
              {renderMedia(selectedItem, true)}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {selectedItem?.post?.eventTitle || 'Untitled event'}
                </Typography>

                {selectedItem?.post?.description && (
                  <Typography variant="body1">
                    {selectedItem.post.description}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ThingsQdosMedia;